// src/features/fx-info/FxInfoPage.tsx
//
// FX Info Deep-Dive Page — Component Islands architecture.
// Extracts the internal FX symbol from the URL query param (?symbol=JPY),
// fetches the pre-computed quant arrays (Vol Cones, Frac-Diff, Seasonality,
// look-ahead-safe COT) from GET /api/v1/fx-info/{symbol}, and renders them.
//
// Indigo accent (indigo-500) distinguishes the institutional FX asset class
// from Commodities (amber), Stocks (blue) and Crypto (orange/emerald).
// Accessed via the "More Info…" action in the FX Ranking detail popup.

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Layout, Sidebar } from '../../shared/ui';
import { fetchFxInfoBySymbol, type FxInfoResponse } from './api';
import {
    FxCotPositioningChart,
    FxSeasonalityHeatmap,
    FxTimeSeriesChart,
    FxTrendExtensionChart,
    FxVolatilityCone,
} from './components';

const formatPct = (val: number) =>
    new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        signDisplay: 'always',
    }).format(val);

const formatDate = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

// Card shell wrapping each visualization island so the tear sheet keeps a
// consistent, institutional look across all four quant panes.
const IslandCard = ({
    children,
    className = '',
}: {
    children: React.ReactNode;
    className?: string;
}) => (
    <div
        className={`rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm ${className}`}
    >
        {children}
    </div>
);

export const FxInfoPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const symbol = searchParams.get('symbol')?.toUpperCase();

    const [info, setInfo] = useState<FxInfoResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!symbol) return;

        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchFxInfoBySymbol(symbol)
            .then((data) => {
                if (!cancelled) setInfo(data);
            })
            .catch((err) => {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error(err);
                setError(`Unable to load analytical data for ${symbol}.`);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol]);

    if (!symbol) {
        return (
            <Layout sidebar={<Sidebar />} title="FX Intelligence">
                <div className="p-6 text-slate-500 dark:text-slate-400">
                    No symbol provided. Please select an asset from the FX
                    Rankings.
                </div>
            </Layout>
        );
    }

    const hasCot =
        !!info &&
        info.time_series.length > 0 &&
        info.time_series.some((p) => p.net_commercial !== null);

    return (
        <Layout
            sidebar={<Sidebar />}
            title={`Deep Dive: ${symbol}`}
            subtitle={info?.name || 'Quantitative FX Analytics'}
        >
            <div className="mb-6">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="text-sm font-medium text-indigo-600 dark:text-indigo-500 hover:underline flex items-center gap-1"
                >
                    &larr; Back to Rankings
                </button>
            </div>

            {error && (
                <div className="mb-6 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="h-64 flex flex-col gap-4 items-center justify-center text-slate-500 dark:text-slate-400 animate-pulse">
                    Computing AFML fractional differencing and risk metrics for{' '}
                    {symbol}…
                </div>
            ) : (
                info && (
                    <div className="space-y-6">
                        {/* --- HEADER ISLAND --- */}
                        <div className="relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 border-l-4 border-l-indigo-500 shadow-sm">
                            {/* subtle institutional accent glow */}
                            <div
                                aria-hidden="true"
                                className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-indigo-500/10 blur-3xl"
                            />
                            <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-3 mb-2">
                                        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
                                            {info.name}
                                        </h1>
                                        <span className="px-2 py-1 bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-300 text-xs font-mono font-bold rounded border border-slate-200 dark:border-slate-700">
                                            FX : {symbol}
                                        </span>
                                        <span className="px-2 py-1 bg-slate-100 dark:bg-slate-900 text-slate-500 dark:text-slate-400 text-xs font-mono rounded border border-slate-200 dark:border-slate-700">
                                            {info.fmp_ticker}
                                        </span>
                                        <span className="px-2 py-1 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 text-xs font-bold rounded border border-indigo-200 dark:border-indigo-800/50">
                                            {info.tier.toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {info.factor_exposures.map((factor) => (
                                            <span
                                                key={factor}
                                                className="text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700/50 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700"
                                            >
                                                {factor}
                                            </span>
                                        ))}
                                    </div>

                                    {/* Market Convention Disclaimer — driven by
                                        the backend invert_price flag. */}
                                    {info.invert_price ? (
                                        <p className="text-xs text-amber-700 dark:text-amber-500 font-medium items-center gap-1.5 bg-amber-50 dark:bg-amber-900/20 px-2.5 py-1 rounded-md inline-flex border border-amber-200 dark:border-amber-800/50">
                                            <svg
                                                className="w-3.5 h-3.5 shrink-0"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                            Market Convention: a rising chart
                                            indicates USD strength and {symbol}{' '}
                                            weakness.
                                        </p>
                                    ) : (
                                        <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium items-center gap-1.5 bg-indigo-50 dark:bg-indigo-900/20 px-2.5 py-1 rounded-md inline-flex border border-indigo-200 dark:border-indigo-800/50">
                                            <svg
                                                className="w-3.5 h-3.5 shrink-0"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                            Market Convention: a rising chart
                                            indicates {symbol} strength against
                                            the USD.
                                        </p>
                                    )}
                                </div>

                                {/* Key Metrics */}
                                <div className="flex items-center gap-6 text-left md:text-right border-t md:border-t-0 md:border-l border-slate-200 dark:border-slate-700 pt-4 md:pt-0 md:pl-6 w-full md:w-auto shrink-0">
                                    <div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wider font-semibold">
                                            Current (Market)
                                        </p>
                                        <p className="text-2xl font-mono text-slate-900 dark:text-white">
                                            {info.current_price.toLocaleString(
                                                'en-US',
                                                {
                                                    minimumFractionDigits: 4,
                                                    maximumFractionDigits: 4,
                                                },
                                            )}
                                        </p>
                                        <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
                                            as of {formatDate(info.as_of_date)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wider font-semibold">
                                            YTD Return
                                        </p>
                                        <p
                                            className={`text-2xl font-mono ${
                                                info.ytd_return >= 0
                                                    ? 'text-emerald-600 dark:text-emerald-400'
                                                    : 'text-red-600 dark:text-red-400'
                                            }`}
                                        >
                                            {formatPct(info.ytd_return)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* --- DATA VISUALIZATION ISLANDS --- */}
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
                            <IslandCard className="flex">
                                <FxVolatilityCone
                                    data={info.volatility_cone}
                                />
                            </IslandCard>
                            <IslandCard className="flex">
                                <FxSeasonalityHeatmap
                                    data={info.seasonality}
                                />
                            </IslandCard>
                        </div>

                        <IslandCard>
                            <FxTimeSeriesChart
                                data={info.time_series}
                                symbol={symbol}
                                invertPrice={info.invert_price}
                            />
                        </IslandCard>

                        {/* Trend Extension & Regime Shift ("The Rubber Band").
                            Derived purely from close / sma_50 / sma_200, so it
                            populates for every currency in the universe — the
                            universal 4th pane that never shows an empty state.
                            Shares syncId="fx-master-sync" with the panes above. */}
                        {info.time_series.length > 0 && (
                            <IslandCard>
                                <FxTrendExtensionChart
                                    data={info.time_series}
                                />
                            </IslandCard>
                        )}

                        {/* Tier-3 Graceful Degradation Guard: only render the
                            COT positioning island when the currency actually
                            has CFTC coverage (net_commercial is non-null).
                            Complements the always-on Trend Extension pane with
                            real speculator flow for the ~11 CFTC-covered majors. */}
                        {hasCot && (
                            <IslandCard>
                                <FxCotPositioningChart
                                    data={info.time_series}
                                />
                            </IslandCard>
                        )}
                    </div>
                )
            )}
        </Layout>
    );
};

export default FxInfoPage;
